import {
    Button,
    Checkbox,
    DatePicker,
    Divider,
    Form,
    Input,
    notification,
    Select,
    Upload,
} from 'antd';
import { useTranslation } from 'react-i18next';
import ImgCrop from 'antd-img-crop';
import { useEffect, useState } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import Sticky from 'react-sticky-el';

import GlobalService from '../../../../services/Global.service';
import Editor from '../../../../components/Shared/forms/EditorPrivacy';
import moment from 'moment';
import BtnSave from './BtnSave';

export const checkValidForm = (fields, values) => {
    for (const [key, value] of Object.entries(values)) {
        if (fields.includes(key) && (!value || value?.length === 0)) {
            return false;
        }
    }
    return true;
};

const Step1 = (props) => {
    const { t, i18n } = useTranslation();
    let lang = i18n.language;
    const { form } = props;
    const values = props.form.getFieldsValue();
    const [fileList, setFileList] = useState([]);
    const [openDs, setOpenDs] = useState(false);
    const [openOhter, setOpenOther] = useState(false);

    const beforeUpload = (file) => {
        const isJpgOrPng =
            file.type === 'image/jpeg' || file.type === 'image/png';

        if (!isJpgOrPng) {
            notification['error']({
                message: t('COOKIE.YouUploadFile'),
            });
        }

        const isLt2M = file.size / 1024 / 1024 < 2;

        if (!isLt2M) {
            notification['error']({
                message: t('COOKIE.ImageMustSmaller'),
            });
        }

        return isJpgOrPng && isLt2M;
    };

    const uploadButton = (
        <div>
            <svg
                width={17}
                height={17}
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M9.16536 5.16732H7.83203V7.83398H5.16536V9.16732H7.83203V11.834H9.16536V9.16732H11.832V7.83398H9.16536V5.16732ZM8.4987 1.83398C4.8187 1.83398 1.83203 4.82065 1.83203 8.50065C1.83203 12.1807 4.8187 15.1673 8.4987 15.1673C12.1787 15.1673 15.1654 12.1807 15.1654 8.50065C15.1654 4.82065 12.1787 1.83398 8.4987 1.83398ZM8.4987 13.834C5.5587 13.834 3.16536 11.4407 3.16536 8.50065C3.16536 5.56065 5.5587 3.16732 8.4987 3.16732C11.4387 3.16732 13.832 5.56065 13.832 8.50065C13.832 11.4407 11.4387 13.834 8.4987 13.834Z"
                    fill="#1890FF"
                />
            </svg>
            <div className="-mt-2 text-blue-500 opacity-90">
                {t('policy.ChooseFile')}
            </div>
        </div>
    );

    useEffect(() => {
        setOpenDs(values?.dataSourceFromCheck?.includes('dataSourceFromDs'));
        setOpenOther(
            values?.dataSourceFromCheck?.includes('dataSourceFromOther')
        );
    }, [values]);

    const onPreview = async (file) => {
        let src = file.url;

        if (!src) {
            src = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj);

                reader.onload = () => resolve(reader.result);
            });
        }

        const image = new Image();
        image.src = src;
        const imgWindow = window.open(src);
        imgWindow?.document.write(image.outerHTML);
    };

    const onNextStep = () => {
        props.nextStep();
        props?.form
            .validateFields()
            .then(() => {
                props.setStep1(true);
            })
            .catch(() => props.setStep1(false));
    };

    const getImage = async (companyLogoId) => {
        try {
            const { data } = await GlobalService.getImage(companyLogoId);
            if (data.resultData?.imageId) {
                setFileList([
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: `data:image/png;base64,${data.resultData?.imageId}`,
                    },
                ]);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (values?.companyLogoId) getImage(values?.companyLogoId);
    }, [values?.companyLogoId]);

    const uploadProps = {
        multiple: false,
        listType: 'picture-card',
        beforeUpload,
        accept: '.png,.jpg,.jpeg',
        className: 'policy',
        fileList,
        onPreview,
        onChange(info) {
            const { status } = info.file;
            if (status === 'uploading') {
                info.file.status = 'done';
            }
            if (status === 'done' || status === 'removed') {
                setFileList(info.fileList);
            }
        },
        onSuccess(res, file) {
            props?.form?.setFieldsValue({
                companyLogoId: res?.imageId,
            });
            getImage(res?.imageId);
        },
        onError(err) {
            console.log('onError', err);
        },
        onRemove() {
            props?.form?.setFieldsValue({
                companyLogoId: null,
            });
        },
        customRequest: async ({ file, onSuccess, onError, onProgress }) => {
            let fileList = new FormData();
            fileList.append('image', file);
            fileList.append('fileName', file.name);
            fileList.append('action', 'image-create-application');

            try {
                const onUploadProgress = ({ total, loaded }) => {
                    onProgress(
                        {
                            percent: Math.round((loaded / total) * 100).toFixed(
                                2
                            ),
                        },
                        file
                    );
                };
                const { data } = await GlobalService.uploadImage(
                    fileList,
                    onUploadProgress
                );
                if (data?.resultData?.imageId) {
                    onSuccess(data?.resultData, file);
                } else {
                    onError('error');
                }
            } catch (error) {
                onError(error);
            }
        },
    };

    useEffect(() => {
        if (props?.previewData?.personalDataCollection) {
            onValuesChange(null, props.previewData?.personalDataCollection);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.previewData]);

    const onValuesChange = (change, allValues) => {
        const fields = [
            'privacyPolicyNoticeNameTh',
            'privacyPolicyNoticeNameEn',
            'effectiveDate',
            'companyNameTh',
            'companyNameEn',
            'privacyPolicyNoticeDataSubjectType',
            'purposeOfDataCollectionTh',
        ];
        const check = checkValidForm(fields, allValues);
        props.setStep1(check);
    };

    return (
        <div className="px-4 block_">
            <div className="text-purple-PHX font-semibold text-2xl">
                {t('policy.des1')}
            </div>
            <div className="text-gray-500 text-tiny">{t('policy.desEtc')}</div>
            <Divider />
            <Form
                scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center',
                }}
                name="personalDataCollection"
                layout="vertical"
                form={form}
                onValuesChange={onValuesChange}
            >
                <Form.Item
                    label={
                        <span className="text-[21px] font-bold">
                            {t('Name of the privacy notice')}
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                    name="privacyPolicyNoticeNameTh"
                    initialValue="นโยบายความเป็นส่วนตัว (Privacy Policy)"
                >
                    <Input addonBefore="TH" size="large" />
                </Form.Item>
                <Form.Item
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                    name="privacyPolicyNoticeNameEn"
                    initialValue="Privacy Policy"
                >
                    <Input addonBefore="EN" size="large" />
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="effectiveDate"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.effectiveDate')}
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                    initialValue={moment()}
                >
                    <DatePicker
                        allowClear={false}
                        size="large"
                        style={{ width: '100%' }}
                        placeholder="DD/MM/YYYY"
                        format="DD/MM/YYYY"
                    />
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="companyNameTh"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.companyNameEn')}
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <Input
                        placeholder="ชื่อองค์กร (หรือชื่อบุคคลเจ้าของ)"
                        addonBefore="TH"
                        size="large"
                    />
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="companyNameEn"
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <Input
                        placeholder="Organization name (or Owner Name)"
                        addonBefore="EN"
                        size="large"
                    />
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="companyLogoId"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.companyLogoId')}
                        </span>
                    }
                >
                    <ImgCrop
                        beforeCrop={beforeUpload}
                        showGrid
                        rotationSlider
                        aspectSlider
                        showReset
                    >
                        <Upload {...uploadProps}>
                            {fileList.length < 1 && uploadButton}
                        </Upload>
                    </ImgCrop>
                </Form.Item>
                <div className="-mt-6 text-gray-400 text-10">
                    {lang === 'en'
                        ? '*Max size of 2 MB. (Allowed file types: jpg., .png.)'
                        : '*ขนาดสูงสุด 2 MB. (ประเภทไฟล์ที่อนุญาต: jpg., .png.)'}
                </div>
                <Form.Item
                    label={
                        <span className="text-[21px] font-bold">
                            {t(
                                'What types of data subjects does this policy apply to'
                            )}
                        </span>
                    }
                    className="mt-4"
                    name="privacyPolicyNoticeDataSubjectType"
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <Select
                        placeholder="เลือกประเภทเจ้าของข้อมูล"
                        style={{ width: '100%' }}
                        size="large"
                        allowClear
                        mode="multiple"
                    >
                        {props.dsType?.map((dsType) => (
                            <Select.Option key={dsType?.id} value={dsType?.id}>
                                {dsType?.nameTh}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    className="mt-4"
                    name="dataSourceFromCheck"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.dataSourceFromCheck')}
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                    initialValue={[]}
                >
                    <Checkbox.Group className="w-full">
                        <Checkbox
                            value="dataSourceFromDs"
                            onChange={(e) => {
                                setOpenDs(!openDs);
                            }}
                        >
                            {t('policy.dataSourceFromDs')}
                        </Checkbox>
                        {openDs && (
                            <div
                                style={{ background: '#EDEEF9' }}
                                className="p-4 rounded-md ml-5 mt-4"
                            >
                                <Form.Item
                                    name="dataSourceFromDsTh"
                                    label={
                                        <span className="text-[14px]">
                                            {t('policy.dataSourceFromDsTh')}
                                        </span>
                                    }
                                >
                                    <Editor
                                        placeholder={t(
                                            'policy.dataSourceFromDsThPl'
                                        )}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="dataSourceFromDsEn"
                                    label={
                                        <span className="text-[14px]">
                                            {t('policy.dataSourceFromDsEn')}
                                        </span>
                                    }
                                >
                                    <Editor
                                        placeholder={t(
                                            'policy.dataSourceFromDsEnPl'
                                        )}
                                    />
                                </Form.Item>
                            </div>
                        )}
                        <br />
                        <Checkbox
                            value="dataSourceFromOther"
                            onChange={(e) => {
                                setOpenOther(!openOhter);
                            }}
                        >
                            {t('policy.dataSourceFromOther')}
                        </Checkbox>
                        {openOhter && (
                            <div
                                style={{ background: '#EDEEF9' }}
                                className="p-4 rounded-md ml-5 my-4"
                            >
                                <Form.Item
                                    name="dataSourceFromOtherTh"
                                    label={
                                        <span className="text-[14px]">
                                            {t('policy.dataSourceFromDsTh')}
                                        </span>
                                    }
                                >
                                    <Editor
                                        placeholder={t(
                                            'policy.dataSourceFromDsThPl'
                                        )}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="dataSourceFromOtherEn"
                                    label={
                                        <span className="text-[14px]">
                                            {t('policy.dataSourceFromDsEn')}
                                        </span>
                                    }
                                >
                                    <Editor
                                        placeholder={t(
                                            'policy.dataSourceFromDsEnPl'
                                        )}
                                    />
                                </Form.Item>
                            </div>
                        )}
                    </Checkbox.Group>
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="purposeOfDataCollectionTh"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.purposeOfDataCollectionTh')}
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <Editor
                        placeholder={t('policy.purposeOfDataCollectionThPl')}
                    />
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="purposeOfDataCollectionEn"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.purposeOfDataCollectionEn')}
                        </span>
                    }
                >
                    <Editor
                        placeholder={t('policy.purposeOfDataCollectionEnPl')}
                    />
                </Form.Item>

                <Sticky
                    mode="bottom"
                    boundaryElement=".block_"
                    stickyClassName="bg-white p-4 shadow h-16 rounded-lg z-50"
                >
                    <div className="flex items-center">
                        <Button
                            type="primary"
                            size="large"
                            onClick={onNextStep}
                        >
                            {t('policy.NextStep')} <ArrowRightOutlined />
                        </Button>
                        <BtnSave saveDraft={props.saveDraft} />
                    </div>
                </Sticky>
            </Form>
        </div>
    );
};
export default Step1;
